import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import QbConfirmReminder from '../components/QbConfirmReminder';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } }
function apiFetch(path, opts = {}) {
  const token = getToken();
  return fetch(`${API_BASE}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', ...(token ? { 'x-worker-token': token } : {}), ...opts.headers } })
    .then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || `Request failed (${r.status})`); return d; });
}

// Dates are handled as plain 'YYYY-MM-DD' strings at noon so a phone's timezone or
// a daylight-saving night can never slide a shift onto the wrong day.
const ymd = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const atNoon = s => new Date(`${s}T12:00:00`);
function weekOf(offset) {
  const d = new Date(); d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7) + offset * 7); // back to Monday
  return Array.from({ length: 7 }, (_, i) => { const x = new Date(d); x.setDate(d.getDate() + i); return ymd(x); });
}
// The shift's own clock time, read straight off the stamp ("2026-09-21T06:30:00-07:00")
// so it shows Reno time no matter where the phone thinks it is.
function clock(stamp) {
  const m = /T(\d{2}):(\d{2})/.exec(stamp || '');
  if (!m) return '';
  const h = Number(m[1]);
  return `${h % 12 || 12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
}

// Worker's own week from the QuickBooks Time schedule. Read-only. Lives with the time clock.
export default function MyScheduleModal({ onClose }) {
  // 0 = this week, 1 = next week. On a Sunday the week is over — open on the one coming.
  const [offset, setOffset] = useState(() => (new Date().getDay() === 0 ? 1 : 0));
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const days = weekOf(offset);
  const today = ymd(new Date());

  useEffect(() => {
    let live = true;
    setData(null); setErr('');
    apiFetch(`/time/my-schedule?start=${days[0]}&end=${days[6]}`)
      .then(d => { if (live) setData(d); })
      .catch(e => { if (live) setErr(e.message); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  const range = `${atNoon(days[0]).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${atNoon(days[6]).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  const navBtn = on => ({ background: on ? '#fff' : '#f3f4f6', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '8px 12px', fontSize: 13, fontWeight: 700, color: on ? '#111' : '#c4c4c4', cursor: on ? 'pointer' : 'default' });

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#111' }}>My Schedule</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, color: '#999', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #f0f0f0' }}>
          <button onClick={() => offset > 0 && setOffset(0)} style={navBtn(offset > 0)}>‹ This week</button>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#111' }}>{offset === 0 ? 'This week' : 'Next week'}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>{range}</div>
          </div>
          <button onClick={() => offset < 1 && setOffset(1)} style={navBtn(offset < 1)}>Next week ›</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '14px 16px 24px' }}>
          {err && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 14 }}>Could not load your schedule. {err}</div>}
          {!err && !data && <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0', fontSize: 14 }}>Loading…</div>}
          {data && data.linked === false && (
            <div style={{ textAlign: 'center', color: '#6b7280', padding: '30px 10px', fontSize: 14 }}>You aren’t linked to the time clock schedule yet — ask the office to check your name in QuickBooks Time.</div>
          )}
          {data && data.linked !== false && (<>
            {days.map(day => {
              const shifts = data.events.filter(e => e.date === day);
              const isToday = day === today;
              return (
                <div key={day} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ width: 48, flexShrink: 0, textAlign: 'center', borderRadius: 10, padding: '6px 0', alignSelf: 'flex-start', background: isToday ? '#e87722' : '#f3f4f6', color: isToday ? '#fff' : '#374151' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{atNoon(day).toLocaleDateString('en-US', { weekday: 'short' })}</div>
                    <div style={{ fontSize: 17, fontWeight: 800, lineHeight: 1.1 }}>{atNoon(day).getDate()}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {shifts.length === 0 && <div style={{ fontSize: 14, color: '#c4c4c4', padding: '10px 0' }}>Nothing scheduled</div>}
                    {shifts.map(s => (
                      <div key={s.id} style={{ border: '1px solid #e5e7eb', borderLeft: '4px solid #2563eb', borderRadius: 10, padding: '9px 12px' }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#111', wordBreak: 'break-word' }}>{s.jobLabel}</div>
                        <div style={{ fontSize: 13, color: '#374151', marginTop: 2 }}>{s.allDay ? 'All day' : `${clock(s.start)} – ${clock(s.end)}`}</div>
                        {s.title && s.title !== s.jobLabel && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3, wordBreak: 'break-word' }}>{s.title}</div>}
                        {s.crew.length > 0 && <div style={{ fontSize: 13.5, color: '#1e3a5f', fontWeight: 700, marginTop: 5 }}>👷 With {s.crew.slice(0, 8).join(', ')}{s.crew.length > 8 ? ` +${s.crew.length - 8} more` : ''}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {offset === 1 && data.events.length === 0 && (
              <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, paddingTop: 14 }}>Next week may not be published yet — check back later.</div>
            )}
          </>)}
        </div>
      </div>
    </div>,
    document.body
  );
}

// The phone home's "Today" card (Ryan, Oct 11 2026 — the crew were asking the AI who they're
// scheduled with): today's job, time and who's with them, straight off the published schedule.
// Tap it for the whole week. Nothing shows for someone not on the QuickBooks Time schedule.
// Clock In right on the card (Aaron's idea, Oct 23 2026): clocks into that scheduled job's
// QuickBooks Time code — the same clock-in the Time Clock does — after a "now?" check so a
// pocket tap can't start a shift. Already on the clock: says so (switching stays on the Time Clock).
export function TodaySchedule() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);     // /time/status — on the clock, and on which code
  const [ask, setAsk] = useState(null);           // the shift whose Clock In was tapped
  const [busy, setBusy] = useState(false);
  const [qbRemind, setQbRemind] = useState(false);  // tap Confirm in QuickBooks Time (see QbConfirmReminder)
  const [err, setErr] = useState('');
  const loadStatus = () => apiFetch('/time/status').then(setStatus).catch(() => setStatus(null));
  useEffect(() => { apiFetch('/time/my-today').then(setD).catch(() => setD(null)); loadStatus(); }, []);
  if (!d || d.linked === false) return null;
  const shifts = (d.events || []).filter(s => !/\boff\b/i.test(s.jobLabel || ''));
  const off = (d.events || []).length > 0 && !shifts.length;
  const on = status && status.clockedIn ? status.shift : null;
  const onThis = sh => on && sh.jobcodeId && String(on.jobcode_id) === String(sh.jobcodeId);

  async function clockIn() {
    setBusy(true); setErr('');
    try {
      const r = await apiFetch('/time/clock-in', { method: 'POST', body: JSON.stringify({ jobcodeId: Number(ask.jobcodeId) }) });
      setAsk(null); setQbRemind(true);
      if (r && r.shift) setStatus({ clockedIn: true, shift: r.shift }); else loadStatus();
      // Same as the Time Clock: a rough location so an assigned truck shows near them on the map.
      if (navigator.geolocation) navigator.geolocation.getCurrentPosition(
        pos => { apiFetch('/me/location', { method: 'POST', body: JSON.stringify({ lat: pos.coords.latitude, lng: pos.coords.longitude }) }).catch(() => {}); },
        () => {}, { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 });
    } catch (e) { setErr(e.message || 'Clock-in failed. Try the Time Clock.'); }
    finally { setBusy(false); }
  }
  const nowClock = () => new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' });

  return (
    <>
      {qbRemind && <QbConfirmReminder onClose={() => setQbRemind(false)} />}
      <div role="button" tabIndex={0} onClick={() => setOpen(true)} onKeyDown={e => { if (e.key === 'Enter') setOpen(true); }}
        style={{ width: '100%', boxSizing: 'border-box', textAlign: 'left', background: '#eff6ff', border: '1.5px solid #bfdbfe', borderLeft: '5px solid #2563eb', borderRadius: 14, padding: '12px 14px', cursor: 'pointer', fontFamily: 'inherit', display: 'block' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: 0.5 }}>📅 Today</span>
          <span style={{ fontSize: 12.5, fontWeight: 800, color: '#1d4ed8' }}>My week ›</span>
        </div>
        {off ? <div style={{ fontSize: 15, fontWeight: 700, color: '#111', marginTop: 4 }}>You’re off today</div>
          : !shifts.length ? <div style={{ fontSize: 15, fontWeight: 600, color: '#6b7280', marginTop: 4 }}>Nothing on the schedule for you today</div>
          : shifts.map(s => (
            <div key={s.id} style={{ marginTop: 6 }}>
              <div style={{ fontSize: 15.5, fontWeight: 800, color: '#111', wordBreak: 'break-word' }}>{s.jobLabel}</div>
              <div style={{ fontSize: 13, color: '#374151' }}>{s.allDay ? 'All day' : `${clock(s.start)} – ${clock(s.end)}`}</div>
              <div style={{ fontSize: 13.5, color: '#1e3a5f', fontWeight: 700, marginTop: 2 }}>{s.crew.length ? `👷 With ${s.crew.slice(0, 6).join(', ')}${s.crew.length > 6 ? ` +${s.crew.length - 6} more` : ''}` : 'Just you on this one'}</div>
              {status && s.jobcodeId && (onThis(s)
                ? <div style={{ marginTop: 8, fontSize: 14, fontWeight: 800, color: '#15803d' }}>✓ On the clock{clock(on.start) ? ` since ${clock(on.start)}` : ''}</div>
                : !on && (
                  <button onClick={e => { e.stopPropagation(); setErr(''); setAsk(s); }}
                    style={{ marginTop: 9, width: '100%', padding: '12px 0', border: 'none', borderRadius: 11, background: '#16a34a', color: '#fff', fontSize: 16, fontWeight: 800, letterSpacing: 0.5, cursor: 'pointer', fontFamily: 'inherit' }}>
                    ⏱ CLOCK IN
                  </button>
                ))}
            </div>
          ))}
        {on && !shifts.some(onThis) && shifts.length > 0 && (
          <div style={{ marginTop: 8, fontSize: 13, fontWeight: 700, color: '#15803d' }}>✓ On the clock{on.jobcodeName ? ` on ${on.jobcodeName}` : ''} — switch jobs on the Time Clock</div>
        )}
      </div>
      {ask && createPortal(
        <div onClick={() => !busy && setAsk(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, padding: '20px 20px 28px', boxSizing: 'border-box' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#15803d', textTransform: 'uppercase', letterSpacing: 0.5 }}>Clock in now — {nowClock()}?</div>
            <div style={{ fontSize: 16.5, fontWeight: 800, color: '#111', marginTop: 6, wordBreak: 'break-word' }}>{ask.jobLabel}</div>
            {!ask.allDay && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>Scheduled {clock(ask.start)} – {clock(ask.end)}</div>}
            {err && <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: '#fef2f2', color: '#991b1b', fontSize: 13, fontWeight: 600 }}>{err}</div>}
            <button disabled={busy} onClick={clockIn}
              style={{ marginTop: 16, width: '100%', padding: '15px 0', border: 'none', borderRadius: 12, background: busy ? '#86efac' : '#16a34a', color: '#fff', fontSize: 17, fontWeight: 800, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit' }}>
              {busy ? 'Clocking in…' : '⏱ Clock In'}
            </button>
            <button disabled={busy} onClick={() => setAsk(null)}
              style={{ marginTop: 8, width: '100%', padding: '12px 0', border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', color: '#374151', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              Cancel
            </button>
          </div>
        </div>,
        document.body
      )}
      {open && <MyScheduleModal onClose={() => setOpen(false)} />}
    </>
  );
}

// A bottom sheet like My Schedule's, for the two smaller lists below.
function Sheet({ title, onClose, children }) {
  return createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#111' }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, color: '#999', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: '14px 16px 24px' }}>{children}</div>
      </div>
    </div>,
    document.body
  );
}
const dayLabel = (s, today) => (s === today ? 'Today' : atNoon(s).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }));

// "Who's with me" (Ryan, Oct 11 2026): the next two weeks of my shifts, each with the names of
// everyone else scheduled on that job that day — big, since that's the question. Read-only.
export function WhoWithMeModal({ onClose }) {
  const today = ymd(new Date());
  const end = (() => { const d = atNoon(today); d.setDate(d.getDate() + 13); return ymd(d); })();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { apiFetch(`/time/my-schedule?start=${today}&end=${end}`).then(setData).catch(e => setErr(e.message)); }, [today, end]);
  const shifts = data && data.events ? data.events.filter(s => !/\boff\b/i.test(s.jobLabel || '')) : [];
  return (
    <Sheet title="👷 Who's with me" onClose={onClose}>
      {err && <div style={{ color: '#dc2626', fontSize: 14 }}>Could not load the schedule. {err}</div>}
      {!err && !data && <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0', fontSize: 14 }}>Loading…</div>}
      {data && data.linked === false && <div style={{ textAlign: 'center', color: '#6b7280', padding: '30px 10px', fontSize: 14 }}>You aren’t linked to the time clock schedule yet — ask the office to check your name in QuickBooks Time.</div>}
      {data && data.linked !== false && !shifts.length && <div style={{ textAlign: 'center', color: '#6b7280', padding: '30px 10px', fontSize: 14 }}>Nothing on the schedule for you in the next two weeks.</div>}
      {shifts.map(s => (
        <div key={s.id} style={{ border: '1px solid #e5e7eb', borderLeft: `4px solid ${s.date === today ? '#e87722' : '#2563eb'}`, borderRadius: 12, padding: '10px 12px', marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: s.date === today ? '#c2410c' : '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>{dayLabel(s.date, today)}{s.allDay ? '' : ` · ${clock(s.start)}`}</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#111', margin: '2px 0 6px', wordBreak: 'break-word' }}>{s.jobLabel}</div>
          {s.crew.length ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {s.crew.map(n => <span key={n} style={{ background: '#eff6ff', color: '#1e3a5f', border: '1px solid #bfdbfe', borderRadius: 999, padding: '4px 10px', fontSize: 13.5, fontWeight: 700 }}>👷 {n}</span>)}
            </div>
          ) : <div style={{ fontSize: 13.5, color: '#6b7280' }}>Just you on this one</div>}
        </div>
      ))}
    </Sheet>
  );
}

// "Upcoming vacations" (Ryan, Oct 11 2026): my own approved time off from today on — and any
// request still waiting, marked as such. Only ever the person's own.
const KIND_LABEL = { vacation: 'Vacation', sick: 'Sick', personal: 'Personal day', other: 'Time off' };
export function UpcomingTimeOffModal({ onClose }) {
  const today = ymd(new Date());
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { apiFetch('/my-time-off').then(r => setRows(Array.isArray(r) ? r : [])).catch(e => setErr(e.message)); }, []);
  const ahead = (rows || []).filter(r => String(r.end_date || r.start_date || '') >= today && r.status !== 'denied')
    .sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)));
  const approved = ahead.filter(r => r.status === 'approved'), waiting = ahead.filter(r => r.status === 'pending');
  const range = r => { const a = atNoon(String(r.start_date).slice(0, 10)), b = atNoon(String(r.end_date || r.start_date).slice(0, 10));
    const f = d => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const n = Math.round((b - a) / 86400000) + 1; return `${f(a)}${n > 1 ? ` – ${f(b)}` : ''} · ${n} day${n === 1 ? '' : 's'}`; };
  const row = (r, tint) => (
    <div key={r.id} style={{ border: '1px solid #e5e7eb', borderLeft: `4px solid ${tint}`, borderRadius: 12, padding: '10px 12px', marginBottom: 10 }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: '#111' }}>{range(r)}</div>
      <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{KIND_LABEL[r.kind] || 'Time off'}{r.reason ? ` · ${r.reason}` : ''}</div>
    </div>
  );
  return (
    <Sheet title="🌴 Upcoming vacations" onClose={onClose}>
      {err && <div style={{ color: '#dc2626', fontSize: 14 }}>Could not load your time off. {err}</div>}
      {!err && !rows && <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0', fontSize: 14 }}>Loading…</div>}
      {rows && !approved.length && <div style={{ textAlign: 'center', color: '#6b7280', padding: '24px 10px', fontSize: 14 }}>No approved time off coming up.</div>}
      {approved.length > 0 && <div style={{ fontSize: 12, fontWeight: 800, color: '#15803d', textTransform: 'uppercase', marginBottom: 8 }}>✓ Approved</div>}
      {approved.map(r => row(r, '#15803d'))}
      {waiting.length > 0 && <>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#b45309', textTransform: 'uppercase', margin: '14px 0 8px' }}>⏳ Waiting for approval</div>
        {waiting.map(r => row(r, '#f59e0b'))}
      </>}
    </Sheet>
  );
}
